import moment from "moment";
import type { TFunction } from "react-i18next";
import type { TransactionRow } from "../pages/transactions/table-config";
import { MEMBER_ROUTE } from "./constants";
import "moment/locale/fr";
import {
	navIconsDefault,
	navIconsActive,
} from "@/assets/images/icons/navigation/index";

/**
 * Returns a scaling function which will normalize a value within the given values array to between newMax (default 0) and newMin (default 1)
 * @param values values to be scaled
 * @returns
 */
export function normalize(
	values: number[],
): (
	val: number,
	newMin?: number,
	newMax?: number,
	constant?: number,
) => number {
	const min = Math.min(...values);
	const max = Math.max(...values);
	return (val: number, newMin = 0, newMax = 1, constant = 0.5) => {
		const clampedVal = Math.min(Math.max(val, min), max);
		const normalized = min < max ? (clampedVal - min) / (max - min) : constant;
		return normalized * (newMax - newMin) + newMin;
	};
}

/**
 * Returns the date value formatted like MM-DD-YYYY
 * @param date to be formatted
 * @returns
 */
// TODO: Replace with Intl.DateTimeFormat
export const formatDate = (date: Date): string => {
	const year = `${date.getFullYear()}`;
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");

	return [month, day, year].join("-");
};

export const createMultipleTableRowData = (
	input: TransactionRow[],
	multiple: number,
): TransactionRow[] => {
	let multipliedRowData: TransactionRow[] = [];
	for (let i = 0; i < multiple; i += 1) {
		multipliedRowData = multipliedRowData.concat(input);
	}
	return multipliedRowData;
};

// Create our number formatter.
const formatter = new Intl.NumberFormat("en-US", {
	notation: "standard",
	// style:'currency',
	// currency: 'USD',

	// These options are needed to round to whole numbers if that's what you want.
	minimumFractionDigits: 2, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
	maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
});

const integerFormatter = new Intl.NumberFormat("en-US", {
	notation: "standard",
	// style:'currency',
	// currency: 'USD',

	// These options are needed to round to whole numbers if that's what you want.
	minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
	maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export const formatAmount = (amount: number | string): string => {
	if (!amount) return "0";
	return formatter.format(Number(amount));
};

export const formatAmountInteger = (amount: number | string): string => {
	if (!amount) return "0";
	return integerFormatter.format(Number(amount));
};

export const formatAmountWithDecimals = (
	amount: number | string | undefined,
	decimals?: number,
): string => {
	if (!amount) return "0";
	if (!decimals) return formatAmount(amount);

	const divisor = 10 ** decimals;
	const amountWithDecimals = Number(amount) / divisor;
	return formatAmount(amountWithDecimals);
};

export const formatEntity = (entity: string): string => {
	if (!entity) return "";
	if (entity === "haiti-pay-test") return "HaitiPay";
	if (entity === "emtech-cli") return "BRH";

	return entity;
};

export const getNavigationItems = (t: TFunction) => {
	const navigationItems = [
		{
			to: MEMBER_ROUTE.GET_STARTED,
			text: t("navigation.home"),
			icon: { default: navIconsDefault.home, active: navIconsActive.home },
		},
		{
			to: MEMBER_ROUTE.DASHBOARD,
			text: t("navigation.dashboard"),
			icon: {
				default: navIconsDefault.dashboard,
				active: navIconsActive.dashboard,
			},
		},
		{
			to: MEMBER_ROUTE.WALLETS,
			text: t("wallets.title"),
			icon: {
				default: navIconsDefault.wallets,
				active: navIconsActive.wallets,
			},
		},
		{
			to: MEMBER_ROUTE.TRANSACTIONS,
			text: t("navigation.transactions"),
			icon: {
				default: navIconsDefault.transactions,
				active: navIconsActive.transactions,
			},
		},
		{
			to: MEMBER_ROUTE.REQUESTS,
			text: t("navigation.requests"),
			icon: {
				default: navIconsDefault.requests,
				active: navIconsActive.requests,
			},
		},
		{
			to: MEMBER_ROUTE.MANAGE,
			text: t("navigation.manage"),
			icon: { default: navIconsDefault.manage, active: navIconsActive.manage },
			subNavigationItems: [
				{
					to: MEMBER_ROUTE.CURRENCY_MANAGEMENT,
					text: t("navigation.currency.management"),
				},
			],
		},
	];

	return navigationItems;
};

export const getNavigationTabs = (
	t: TFunction,
): { title: string; route: string }[] => [
	{
		title: t("overview.title"),
		route: "overview",
	},
	{
		title: t("international.tab.title"),
		route: "international",
	},
	{
		title: t("local.tab.title"),
		route: "local",
	},
];

export const getTransactionTabs = (
	t: TFunction,
): { title: string; route: string }[] => [
	{
		title: t("internal.tab.title"),
		route: "internal",
	},
	{
		title: t("external.tab.title"),
		route: "external",
	},
];

export const getMonthFromTimestamp = (time: number, locale: string): string => {
	moment.locale([locale, "en"]);
	return moment(time).format("Do MMM");
};

export const getDateFromTimestamp = (time: number, _t: TFunction): string =>
	moment(Number(time)).format("ddd Do");

export const getDateMonthFromTimestamp = (
	time: number,
	locale = "en" as string,
): string => {
	moment.locale([locale, "en"]);
	return moment(time).format("Do MMM YYYY");
};

export const convertIsoDate = (isoDateString: string) => {
  return moment(isoDateString)
    .format('Do MMM YYYY, h:mm a');
}

export const getDateMonthTimeFromTimestamp = (
	time: number,
	locale = "en" as string,
): string => {
	moment.locale([locale, "en"]);
	return moment(time).format("Do MMM YYYY, h:mm a");
};
// return String(date.getDate());

export const setWithExpiry = (key, value, ttl) => {
	const now = new Date();

	// `item` is an object which contains the original value
	// as well as the time when it's supposed to expire
	const item = {
		value,
		expiry: now.getTime() + ttl,
	};
	localStorage.setItem(key, JSON.stringify(item));
};

export const getWithExpiry = (key) => {
	const itemStr = localStorage.getItem(key);

	// if the item doesn't exist, return null
	if (!itemStr) {
		return null;
	}

	const item = JSON.parse(itemStr);
	const now = new Date();

	// compare the expiry time of the item with the current time
	if (now.getTime() > item.expiry) {
		// If the item is expired, delete the item from storage
		// and return null
		localStorage.removeItem(key);
		return null;
	}
	return item.value;
};

export const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  
  const stringWithSpaces = str.replace(/_/g, ' ');
  if (stringWithSpaces.length === 1) return stringWithSpaces.toUpperCase();
  const firstChar = stringWithSpaces.charAt(0).toUpperCase();
  const restOfString = stringWithSpaces.slice(1).toUpperCase();
  return `${firstChar}${restOfString}`;
};

export const  removeDelimeterFromString = (input: string, delimiter = '_'): string => {
  if (!input) {
      return input || '';
  }

  return input.split(delimiter)
      .filter(part => part.length > 0)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
}
